import { z } from "zod";
import { SpotifyIdSchema, jsonColumn } from "./common.ts";

export const AlbumSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string().nullable(),
  release_date: z.string().nullable(),
  release_date_precision: z.enum(["year", "month", "day"]).nullable(),
  album_type: z.enum(["album", "single", "compilation"]).nullable(),
  total_tracks: z.number().int().nullable(),
  cover_url: z.string().nullable(),
});

export type Album = z.infer<typeof AlbumSchema>;

/**
 * Browse → Albums. Counts / artists are derived in SQL from the local mirror
 * (no Spotify API). Albums with zero mirrored tracks are omitted.
 */
export const AlbumBrowseMatchViaSchema = z.enum(["name", "artist", "track"]);

export const AlbumArtistRefSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string().nullable(),
});

export const AlbumBrowseItemSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string().nullable(),
  cover_url: z.string().nullable(),
  album_type: z.enum(["album", "single", "compilation"]).nullable(),
  /** First 4 chars of `release_date` when present; null otherwise. */
  release_year: z.number().int().nullable(),
  /** Tracks in `tracks` that point at this album. */
  track_count: z.number().int().nonnegative(),
  /** Credits from library tracks on this album (clickable in the browse table). */
  artists: jsonColumn(z.array(AlbumArtistRefSchema)).default([]),
  /**
   * Top user-taxonomy genre ids on this album’s mirrored tracks (≤5).
   * Used by Browse → Albums genre filter (hierarchy expanded client-side).
   */
  genre_ids: jsonColumn(z.array(z.string())).default([]),
  match_via: AlbumBrowseMatchViaSchema.nullable().optional().default(null),
  match_label: z.string().nullable().optional().default(null),
});

export type AlbumBrowseItem = z.infer<typeof AlbumBrowseItemSchema>;
export type AlbumBrowseMatchVia = z.infer<typeof AlbumBrowseMatchViaSchema>;
export type AlbumArtistRef = z.infer<typeof AlbumArtistRefSchema>;
